// ============================================
// FILE: src/components/CategoryProductsScreen.jsx
// ============================================

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { ArrowLeft, Search, Heart, Plus, ShoppingCart } from 'lucide-react';
import ProductDetailsModal from './modals/ProductDetailsModal';
import { categoryIcons } from '../data/products';
import { getProductImage, resolveProductImageUrl } from '../utils/imageHelpers';
import { getFinalPrice, hasVariants, getVariantPriceFrom } from '../utils/priceHelpers';
import apiService from '../apiService';
import QuantityTextBox from './QuantityTextBox';
import { usesQuantityTextBox } from '../utils/quantityHelpers';

const SORT_OPTIONS = [
  { value: 'newest',     label: 'Newest',           sort_by: 'created_at', sort_order: 'desc' },
  { value: 'price_asc',  label: 'Price: Low → High', sort_by: 'price',      sort_order: 'asc'  },
  { value: 'price_desc', label: 'Price: High → Low', sort_by: 'price',      sort_order: 'desc' },
  { value: 'name_asc',   label: 'Name A–Z',          sort_by: 'name',       sort_order: 'asc'  },
];

const PER_PAGE = 20;

const CategoryProductsScreen = ({
  category,
  onBack,
  cart = [],
  addToCart,
  updateQuantity,
  getCartCount,
  getCartTotal,
  openCheckout,
  toggleFavorite,
  favorites = [],
  setShowSubscription,
  showToast,
  appTheme,
  isDesktop = false,
  headerSlot = null,
}) => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [sortBy, setSortBy] = useState('newest');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalProducts, setTotalProducts] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [quantitySelections, setQuantitySelections] = useState({});
  const [quantityLoading, setQuantityLoading] = useState({});
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [showProductDetails, setShowProductDetails] = useState(false);

  const debounceRef = useRef(null);

  const favArray = Array.isArray(favorites) ? favorites : [];
  const isProductFavorited = (productId) =>
    favArray.some((item) => String(item?.id) === String(productId));

  const getSortParams = (sortValue) => {
    const opt = SORT_OPTIONS.find(o => o.value === sortValue) || SORT_OPTIONS[0];
    return { sort_by: opt.sort_by, sort_order: opt.sort_order };
  };

  const parseResponse = (resp) => {
    const paginatedObj = resp?.data?.products || resp?.data;
    const items = Array.isArray(paginatedObj?.data) ? paginatedObj.data
      : Array.isArray(paginatedObj) ? paginatedObj
      : Array.isArray(resp) ? resp
      : [];
    const lastPage = paginatedObj?.last_page || 1;
    const total = paginatedObj?.total || items.length;
    return { items, lastPage, total };
  };

  const fetchInitialProducts = useCallback(async (search, sort) => {
    setLoading(true);
    setProducts([]);
    setCurrentPage(1);
    setHasMore(false);
    try {
      const { sort_by, sort_order } = getSortParams(sort);
      const resp = await apiService.getProductsByCategory(category.id, {
        search, sort_by, sort_order, per_page: PER_PAGE, page: 1,
      });
      const { items, lastPage, total } = parseResponse(resp);
      setProducts(items);
      setTotalProducts(total);
      setHasMore(lastPage > 1);
      setCurrentPage(1);
    } catch {
      setProducts([]);
      setTotalProducts(0);
    } finally {
      setLoading(false);
    }
  }, [category.id]);

  const loadMore = async () => {
    if (loadingMore || !hasMore) return;
    setLoadingMore(true);
    try {
      const { sort_by, sort_order } = getSortParams(sortBy);
      const nextPage = currentPage + 1;
      const resp = await apiService.getProductsByCategory(category.id, {
        search: searchQuery, sort_by, sort_order, per_page: PER_PAGE, page: nextPage,
      });
      const { items, lastPage } = parseResponse(resp);
      setProducts(prev => [...prev, ...items]);
      setCurrentPage(nextPage);
      setHasMore(nextPage < lastPage);
    } catch {
      // keep current state on error
    } finally {
      setLoadingMore(false);
    }
  };

  // Debounce search + sort changes, re-fetch from page 1
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      fetchInitialProducts(searchQuery, sortBy);
    }, 400);
    return () => clearTimeout(debounceRef.current);
  }, [searchQuery, sortBy, fetchInitialProducts]);

  // Quantity helpers (same logic as HomeScreen)
  const getWeightParts = (product) => {
    if (!product) return { value: null, unit: '' };
    if (product.weight !== undefined && product.weight !== null && product.weight !== '') {
      const raw = String(product.weight).trim();
      const match = raw.match(/^(\d*\.?\d+)\s*([a-zA-Z]*)$/);
      if (match) return { value: parseFloat(match[1]), unit: match[2] || product.unit || '' };
    }
    return { value: null, unit: product.unit || '' };
  };

  const isWeightUnit = (unit) => {
    if (!unit) return false;
    return ['kg', 'gram', 'g'].includes(String(unit).toLowerCase().trim());
  };

  const formatUnit = (unit) => {
    if (!unit) return '';
    const str = String(unit).trim();
    return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
  };

  const getQuantityOptions = (product) => {
    const { unit } = getWeightParts(product);
    return isWeightUnit(unit) ? [0.5, 1, 2, 3] : [1, 2, 3, 4];
  };

  const formatWeightLabel = (product) => {
    const { value, unit } = getWeightParts(product);
    if (value) return `${value}${unit ? ` ${unit}` : ''}`;
    return product.weight ? String(product.weight) : '';
  };

  const getSelectedQuantity = (productId) => quantitySelections[productId] || 1;

  const setSelectedQuantity = (productId, qty) => {
    setQuantitySelections(prev => ({ ...prev, [productId]: qty }));
  };

  const handleAddToCartWithQuantity = (product) => {
    addToCart(product, getSelectedQuantity(product.id));
  };

  const catImageUrl = resolveProductImageUrl(category.image);

  // Loading skeleton
  const Skeleton = () => (
    <div className={`grid gap-4 ${isDesktop ? 'grid-cols-4 xl:grid-cols-5 max-w-[1280px] mx-auto' : 'grid-cols-2'}`}>
      {Array.from({ length: isDesktop ? 10 : 6 }).map((_, i) => (
        <div key={i} className="rounded overflow-hidden bg-white animate-pulse">
          <div className="h-44 bg-gray-200" />
          <div className="p-3 space-y-2">
            <div className="h-3 bg-gray-200 rounded w-3/4" />
            <div className="h-3 bg-gray-200 rounded w-1/2" />
            <div className="h-8 bg-gray-200 rounded" />
          </div>
        </div>
      ))}
    </div>
  );

  return (
    <div className="fixed inset-0 z-[220] bg-white flex flex-col" style={{ maxWidth: isDesktop ? '100%' : 448, margin: '0 auto' }}>

      {/* Global desktop navbar (injected by App) */}
      {headerSlot}

      {/* Sticky Header */}
      <div className="sticky top-0 z-10 bg-white" style={{ boxShadow: '0 2px 12px rgba(0,0,0,0.08)' }}>
       <div className={isDesktop ? 'max-w-[1280px] mx-auto' : ''}>
        {/* Title row */}
        <div className="flex items-center gap-3 px-4 pt-3 pb-2">
          <button
            onClick={onBack}
            className="p-2 rounded-full hover:bg-gray-100 transition-colors flex-shrink-0"
          >
            <ArrowLeft size={22} className="text-[#535766]" />
          </button>

          {catImageUrl ? (
            <img
              src={catImageUrl}
              alt={category.name}
              className="w-10 h-10 rounded-full object-cover flex-shrink-0"
              onError={(e) => { e.target.style.display = 'none'; }}
            />
          ) : (
            <div
              className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0"
              style={{ background: 'linear-gradient(135deg,#ff3f6c,#e03a60)' }}
            >
              <span className="text-xl leading-none">{categoryIcons[category.name] || '📦'}</span>
            </div>
          )}

          <h1 className="text-lg font-bold text-[#282c3f] flex-1 truncate">{category.name}</h1>
        </div>

        {/* Search bar */}
        <div className="px-4 pb-2">
          <div className="flex items-center gap-2 px-4 py-2 rounded" style={{ background: '#f2f2f2' }}>
            <Search size={15} className="text-[#94969f] flex-shrink-0" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder={`Search in ${category.name}…`}
              className="flex-1 bg-transparent text-sm text-[#535766] outline-none placeholder-gray-400"
            />
            {searchQuery && (
              <button
                onClick={() => setSearchQuery('')}
                className="text-[#94969f] hover:text-[#535766] flex-shrink-0 text-base leading-none"
              >
                ✕
              </button>
            )}
          </div>
        </div>

        {/* Sort pills */}
        <div className="flex gap-2 px-4 pb-3 overflow-x-auto hide-scrollbar">
          {SORT_OPTIONS.map(opt => (
            <button
              key={opt.value}
              onClick={() => setSortBy(opt.value)}
              className="px-3 py-1.5 rounded-full text-xs font-semibold whitespace-nowrap transition-colors flex-shrink-0"
              style={sortBy === opt.value
                ? { background: '#ff3f6c', color: '#fff' }
                : { background: '#f2f2f2', color: '#555' }}
            >
              {opt.label}
            </button>
          ))}
        </div>
       </div>
      </div>

      {/* Product count */}
      {!loading && (
        <div className={`py-2 text-xs text-[#94969f] px-4 ${isDesktop ? 'max-w-[1280px] mx-auto w-full' : ''}`}>
          Showing {products.length} of {totalProducts} products
        </div>
      )}

      {/* Scrollable product area */}
      <div className="flex-1 overflow-y-auto px-4 pb-4" style={{ background: '#f7f7f7' }}>

        {loading && <div className="pt-3"><Skeleton /></div>}

        {!loading && products.length === 0 && (
          <div className="flex flex-col items-center justify-center py-20 text-center">
            <span className="text-6xl mb-4">🔍</span>
            <p className="text-[#94969f] font-semibold text-base">No products found</p>
            <p className="text-[#94969f] text-sm mt-1">Try a different search or sort</p>
          </div>
        )}

        {!loading && products.length > 0 && (
          <div className={`grid gap-4 pt-3 ${isDesktop ? 'grid-cols-4 xl:grid-cols-5 max-w-[1280px] mx-auto' : 'grid-cols-2'}`}>
            {products.map((product, index) => {
              const isFav = isProductFavorited(product.id);
              const hasDiscount = Number(product.sale_price) > 0 && Number(product.price) > 0
                && Number(product.sale_price) < Number(product.price);
              const offPercent = hasDiscount
                ? Math.round(((Number(product.price) - Number(product.sale_price)) / Number(product.price)) * 100)
                : 0;
              return (
                <div
                  key={product.id}
                  className="rounded overflow-hidden relative glass-card"
                  style={{ animationDelay: `${(index % 10) * 50}ms`, background: '#fff' }}
                >
                  <div
                    className="relative overflow-hidden h-44 cursor-pointer group"
                    onClick={() => { setSelectedProduct(product); setShowProductDetails(true); }}
                  >
                    {getProductImage(product, 'card')}
                    {hasDiscount && (
                      <span className="absolute top-3 left-3 text-xs bg-[#ff3f6c] text-white px-2 py-0.5 rounded-full font-semibold shadow-sm ring-1 ring-white/70">
                        {offPercent}% OFF
                      </span>
                    )}
                    <button
                      onClick={(e) => { e.stopPropagation(); if (typeof toggleFavorite === 'function') toggleFavorite(product); }}
                      className="absolute top-3 right-3 p-2 bg-white rounded-full shadow hover:scale-105 transition-transform"
                    >
                      <Heart size={18} className={isFav ? 'text-pink-500' : 'text-[#94969f]'} fill={isFav ? 'currentColor' : 'none'} />
                    </button>
                  </div>

                  <div className="p-3">
                    <h3
                      className="font-semibold text-[#282c3f] text-sm mb-1 cursor-pointer hover:text-[#ff3f6c] transition-colors line-clamp-2"
                      onClick={() => { setSelectedProduct(product); setShowProductDetails(true); }}
                    >
                      {product.name}
                    </h3>

                    <div className="flex items-center gap-2 mb-2">
                      {product.weight && (
                        <span className="text-xs bg-gray-100 text-[#535766] px-2 py-1 rounded">{formatWeightLabel(product)}</span>
                      )}
                      {!product.weight && product.fat && (
                        <span className="text-xs bg-[#e8f7f3] text-[#03a685] px-2 py-1 rounded">{product.fat} fat</span>
                      )}
                      {!product.weight && !product.fat && product.brand && (
                        <span className="text-xs bg-gray-100 text-[#535766] px-2 py-1 rounded">{product.brand}</span>
                      )}
                    </div>

                    <div className="flex items-center gap-2 mb-2">
                      {hasVariants(product) ? (
                        <span className="text-sm font-extrabold text-emerald-600">from ₹{getVariantPriceFrom(product)}</span>
                      ) : product.sale_price && product.sale_price < product.price ? (
                        <>
                          <span className="text-sm font-extrabold text-[#03a685]">₹{product.sale_price}</span>
                          <span className="text-xs text-[#94969f] line-through">₹{product.price}</span>
                        </>
                      ) : (
                        <span className="text-sm font-extrabold text-emerald-600">₹{getFinalPrice(product)}</span>
                      )}
                    </div>

                    <div className="flex flex-col gap-2">
                      <div className="flex flex-wrap items-center gap-2">
                        {usesQuantityTextBox(product) ? (
                          <QuantityTextBox
                            value={getSelectedQuantity(product.id)}
                            onChange={(qty) => setSelectedQuantity(product.id, qty)}
                          />
                        ) : (
                          getQuantityOptions(product).map((qty) => (
                            <button
                              key={qty}
                              type="button"
                              onClick={() => setSelectedQuantity(product.id, qty)}
                              className={`text-xs px-2 py-1 rounded-full border transition-colors ${
                                getSelectedQuantity(product.id) === qty
                                  ? 'bg-[#ff3f6c] text-white border-[#ff3f6c]'
                                  : 'bg-white text-[#535766] border-[#eaeaec] hover:border-[#ff3f6c]'
                              }`}
                            >
                              {qty}
                            </button>
                          ))
                        )}
                        {isWeightUnit(getWeightParts(product).unit) && (
                          <span className="text-xs text-[#535766] ml-1 font-medium">Kg</span>
                        )}
                        {!isWeightUnit(getWeightParts(product).unit) && getWeightParts(product).unit && (
                          <span className="text-xs text-[#535766] ml-1 font-medium">{formatUnit(getWeightParts(product).unit)}</span>
                        )}
                      </div>

                      {(() => {
                        const cartItem = cart.find(item => item.id === product.id);
                        if (cartItem && cartItem.quantity > 0) {
                          return (
                            <div className="w-full flex items-center justify-center gap-2 bg-[#fff8fa] border border-[#ff3f6c] rounded-lg px-2 py-1 mt-1">
                              <button
                                className="flex items-center justify-center w-8 h-8 rounded-full bg-white border border-[#ff3f6c] text-[#ff3f6c] text-xl font-bold shadow-sm hover:bg-[#fff8fa] focus:outline-none disabled:opacity-50"
                                disabled={quantityLoading[product.id] || cartItem.quantity <= 1}
                                onClick={async () => {
                                  if (typeof updateQuantity === 'function' && cartItem.quantity > 1) {
                                    setQuantityLoading(prev => ({ ...prev, [product.id]: true }));
                                    await updateQuantity(product.id, -1);
                                    setQuantityLoading(prev => ({ ...prev, [product.id]: false }));
                                  }
                                }}
                              >
                                <span>&#8722;</span>
                              </button>
                              <span className="text-base font-bold text-[#ff3f6c] w-10 text-center select-none">
                                {quantityLoading[product.id] ? '…' : cartItem.quantity}
                              </span>
                              <button
                                className="flex items-center justify-center w-8 h-8 rounded-full bg-white border border-[#ff3f6c] text-[#ff3f6c] text-xl font-bold shadow-sm hover:bg-[#fff8fa] focus:outline-none disabled:opacity-50"
                                disabled={!!quantityLoading[product.id]}
                                onClick={async () => {
                                  if (typeof updateQuantity === 'function' && !quantityLoading[product.id]) {
                                    setQuantityLoading(prev => ({ ...prev, [product.id]: true }));
                                    await updateQuantity(product.id, 1);
                                    setQuantityLoading(prev => ({ ...prev, [product.id]: false }));
                                  }
                                }}
                              >
                                <span>&#43;</span>
                              </button>
                            </div>
                          );
                        }
                        return (
                          <button
                            onClick={() => handleAddToCartWithQuantity(product)}
                            className="w-full bg-[#ff3f6c] text-white py-2 rounded-lg text-sm font-medium hover:bg-[#e03a60] transition-colors flex items-center justify-center gap-1"
                          >
                            <Plus size={16} />
                            Add to Cart
                          </button>
                        );
                      })()}
                    </div>

                    {Boolean(Number(product.is_subscribable)) && (
                      <button
                        onClick={() => setShowSubscription && setShowSubscription(product)}
                        className="w-full bg-[#03a685] text-white py-2 rounded-lg text-sm font-medium hover:bg-[#028f73] transition-colors mt-2"
                      >
                        Subscribe &amp; Save
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Load More */}
        {!loading && hasMore && (
          <button
            onClick={loadMore}
            disabled={loadingMore}
            className={`mt-6 py-3 rounded border font-semibold text-sm transition-colors ${isDesktop ? 'block mx-auto px-16' : 'w-full'}`}
            style={{ borderColor: '#ff3f6c', color: loadingMore ? '#aaa' : '#ff3f6c', background: '#fff' }}
          >
            {loadingMore ? 'Loading…' : 'Load More'}
          </button>
        )}

        {loadingMore && (
          <p className="text-center text-xs text-[#94969f] mt-3">Loading more products…</p>
        )}
      </div>

      {/* Bottom checkout bar — pinned inside this screen, no fixed overlap */}
      {cart.length > 0 && typeof getCartCount === 'function' && getCartCount() > 0 && (
        <div className={`px-4 py-3 bg-white border-t border-gray-100 ${isDesktop ? 'max-w-[1280px] mx-auto w-full' : ''}`} style={{ boxShadow: '0 -4px 16px rgba(0,0,0,0.08)' }}>
          <button
            onClick={openCheckout}
            className="w-full bg-[#ff3f6c] text-white py-3 rounded font-bold flex items-center justify-center gap-3 shadow-lg"
          >
            <ShoppingCart size={20} />
            <span>Checkout Now</span>
            <span className="bg-white/20 px-2 py-0.5 rounded-full text-sm">
              {getCartCount()} {getCartCount() === 1 ? 'item' : 'items'}</span>
          </button>
        </div>
      )}

      {/* Product Details Modal */}
      {showProductDetails && selectedProduct && (
        <ProductDetailsModal
          getCartCount={getCartCount}
          getCartTotal={getCartTotal}
          openCheckout={openCheckout}
          isOpen={showProductDetails}
          product={selectedProduct}
          onClose={() => { setShowProductDetails(false); setSelectedProduct(null); }}
          addToCart={addToCart}
          cart={cart}
          updateQuantity={updateQuantity}
          toggleFavorite={toggleFavorite}
          favorites={favorites}
          isFavorited={isProductFavorited(selectedProduct?.id)}
          setShowSubscription={setShowSubscription}
          showToast={showToast}
          appTheme={appTheme}
        />
      )}
    </div>
  );
};

export default CategoryProductsScreen;
